/**
 * Aneks do umowy — nowe warunki z datą obowiązywania (Etap 14).
 *
 * Formularz nie zmienia akt od razu: tworzy PROPOZYCJĘ, którą pracownik przyjmuje
 * albo odrzuca w portalu (porozumienie stron — art. 11 k.p. i art. 353(1) k.c.
 * w zw. z art. 300 k.p.; art. 42 k.p. dotyczy wypowiedzenia zmieniającego).
 * Pola, których formularz nie zmienia, przechodzą z warunków obowiązujących
 * w dniu wejścia aneksu (grupa 23). Data obowiązywania
 * jest osią całej zmiany — silnik płac liczy po niej, więc aneks od lipca nie
 * rusza czerwcowej listy.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, FormField, InfoBanner, Input, Modal, Select, useToast } from '@k1p4/ui'
import { CONTRACT_TYPES, isEmploymentContract } from '@k1p4/shared'
import { ApiError, api, type EmployeeProfileResponse } from '../../api/client'
import styles from './EmployeeProfile.module.css'
import { useFormat } from '../../format'

export interface TermsChangeModalProps {
  employeeId: string
  /**
   * Ostatnia przyjęta wersja warunków — wypełnia formularz, żeby zmieniać jedno
   * pole, nie wszystkie. Aneks nie może zaczynać się przed nią, więc to ona jest
   * bazą, a nie warunki z dzisiaj (przegląd Fali 6).
   */
  current: EmployeeProfileResponse['employmentTerms']
  onClose: () => void
}

export function TermsChangeModal({ employeeId, current, onClose }: TermsChangeModalProps) {
  const fmt = useFormat()
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    position: useId(),
    contractType: useId(),
    fte: useId(),
    startDate: useId(),
    baseSalary: useId(),
    bonus: useId(),
    workplace: useId(),
    workMode: useId(),
    reason: useId(),
  }

  const [form, setForm] = useState({
    position: current?.position ?? '',
    contractType: current?.contractType ?? 'uop_nieokreslony',
    fte: current?.fte ?? '1.00',
    startDate: '',
    baseSalary: current?.baseSalaryGr ? String(current.baseSalaryGr / 100) : '',
    bonus: current?.bonusGr ? String(current.bonusGr / 100) : '',
    workplace: current?.workplace ?? '',
    workMode: current?.workMode ?? '',
    reason: '',
  })
  const [error, setError] = useState<{ key: string; date?: string } | null>(null)

  /*
   * Aneks nie może obowiązywać przed obecnymi warunkami — API odmawia, bo taki
   * aneks nigdy nie wszedłby do akt, a silnik płac przesunąłby wstecz początek
   * zatrudnienia. Kalendarz od razu nie proponuje wcześniejszych dni.
   */
  const notBefore = current?.startDate ?? undefined
  const tooEarly =
    notBefore !== undefined && form.startDate.length > 0 && form.startDate < notBefore

  /* Etat to pojęcie umowy o pracę — przy zleceniu, dziele i B2B go nie ma (Fala 6). */
  const employment = isEmploymentContract(form.contractType)
  /*
   * Rodzina umowy z ostatnich warunków wyznacza, co da się zmienić aneksem
   * (fala 9b, ADR-0015): umowę o pracę zmienia się w obrębie umów o pracę,
   * a umowę cywilną (art. 77 k.c.) — bez zmiany jej rodzaju; przejście to
   * ustanie i nowa umowa, więc formularz takich opcji nie proponuje.
   */
  const baseType = current?.contractType ?? 'uop_nieokreslony'
  const baseEmployment = isEmploymentContract(baseType)
  const contractOptions = baseEmployment ? CONTRACT_TYPES.filter(isEmploymentContract) : [baseType]

  const propose = useMutation({
    mutationFn: () =>
      api.proposeTermsChange(employeeId, {
        position: form.position.trim(),
        fte: employment ? Number(form.fte) : 1,
        startDate: form.startDate,
        contractType: form.contractType,
        baseSalaryGr: Math.round(Number(form.baseSalary) * 100),
        /*
         * Puste pole to „usuń" — pominięcie znaczyłoby „bez zmian" (grupa 23).
         * Premia stała to składnik umowy o pracę; umowa cywilna niesie ją bez zmian.
         */
        bonusGr: employment
          ? form.bonus.trim()
            ? Math.round(Number(form.bonus) * 100)
            : null
          : (current?.bonusGr ?? null),
        workplace: form.workplace.trim() || null,
        workMode: form.workMode.trim() || null,
        reason: form.reason.trim(),
      }),
    onSuccess: () => {
      toast.show(t('profile.annex.proposed'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] })
      onClose()
    },
    onError: (cause: unknown) => {
      if (cause instanceof ApiError && cause.code === 'TERMS_PENDING') {
        setError({ key: 'profile.annex.errorPending' })
      } else if (cause instanceof ApiError && cause.code === 'NOT_EMPLOYED') {
        setError({ key: 'profile.annex.errorNotEmployed' })
      } else if (cause instanceof ApiError && cause.issues.includes('contract_family_change')) {
        setError({ key: 'profile.annex.errorContractFamily' })
      } else if (cause instanceof ApiError && cause.issues.includes('probation_requires_new_contract')) {
        setError({ key: 'profile.annex.errorProbation' })
      } else if (cause instanceof ApiError && cause.issues.includes('before_contract_start')) {
        /*
         * Tu trafiamy, gdy serwer zna warunki świeższe niż ekran albo granicą
         * jest data zatrudnienia z akt (warunki bez daty).
         */
        setError(
          notBefore
            ? { key: 'profile.annex.errorBeforeStart', date: notBefore }
            : { key: 'profile.annex.errorBeforeHire' },
        )
      } else {
        setError({ key: 'profile.annex.errorGeneric' })
      }
    },
  })

  const ready =
    form.position.trim().length >= 2 &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.startDate) &&
    !tooEarly &&
    Number(form.baseSalary) > 0 &&
    form.reason.trim().length >= 5

  return (
    <Modal
      open
      title={t('profile.annex.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!ready || propose.isPending} onClick={() => propose.mutate()}>
            {t('profile.annex.submit')}
          </Button>
        </>
      }
    >
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.annexBanner}>
          {t(error.key, { date: error.date ? fmt.date(error.date, 'long') : '—' })}
        </InfoBanner>
      ) : null}
      {baseEmployment ? null : (
        <InfoBanner tone="info" className={styles.annexBanner}>
          {t('profile.annex.civilNote')}
        </InfoBanner>
      )}
      <FormField
        label={t('profile.annex.startDate')}
        htmlFor={ids.startDate}
        hint={t('profile.annex.startDateHint')}
        {...(tooEarly && notBefore
          ? { error: t('profile.annex.errorBeforeStart', { date: fmt.date(notBefore, 'long') }) }
          : {})}
      >
        <Input
          id={ids.startDate}
          type="date"
          {...(notBefore ? { min: notBefore } : {})}
          invalid={tooEarly}
          value={form.startDate}
          onChange={(event) => setForm({ ...form, startDate: event.target.value })}
        />
      </FormField>
      <FormField label={t('profile.fields.position')} htmlFor={ids.position}>
        <Input
          id={ids.position}
          value={form.position}
          onChange={(event) => setForm({ ...form, position: event.target.value })}
        />
      </FormField>
      <FormField label={t('profile.annex.contractType')} htmlFor={ids.contractType}>
        <Select
          id={ids.contractType}
          value={form.contractType}
          onChange={(event) =>
            setForm({ ...form, contractType: event.target.value as typeof form.contractType })
          }
        >
          {contractOptions.map((type) => (
            <option key={type} value={type}>
              {t(`contractType.${type}`)}
            </option>
          ))}
        </Select>
      </FormField>
      {employment ? (
        <FormField label={t('profile.fields.fte')} htmlFor={ids.fte}>
          <Input
            id={ids.fte}
            value={form.fte}
            onChange={(event) => setForm({ ...form, fte: event.target.value })}
          />
        </FormField>
      ) : null}
      <FormField
        label={t(employment ? 'profile.annex.baseSalary' : 'profile.annex.civilAmount')}
        htmlFor={ids.baseSalary}
      >
        <Input
          id={ids.baseSalary}
          inputMode="decimal"
          value={form.baseSalary}
          onChange={(event) => setForm({ ...form, baseSalary: event.target.value })}
        />
      </FormField>
      {employment ? (
        <FormField label={t('profile.annex.bonus')} htmlFor={ids.bonus}>
          <Input
            id={ids.bonus}
            inputMode="decimal"
            value={form.bonus}
            onChange={(event) => setForm({ ...form, bonus: event.target.value })}
          />
        </FormField>
      ) : null}
      <FormField label={t('profile.annex.workplace')} htmlFor={ids.workplace}>
        <Input
          id={ids.workplace}
          value={form.workplace}
          onChange={(event) => setForm({ ...form, workplace: event.target.value })}
        />
      </FormField>
      <FormField label={t('profile.annex.workMode')} htmlFor={ids.workMode}>
        <Input
          id={ids.workMode}
          value={form.workMode}
          onChange={(event) => setForm({ ...form, workMode: event.target.value })}
        />
      </FormField>
      <FormField
        label={t('profile.annex.reason')}
        htmlFor={ids.reason}
        hint={t('profile.annex.reasonHint')}
      >
        <Input
          id={ids.reason}
          value={form.reason}
          onChange={(event) => setForm({ ...form, reason: event.target.value })}
        />
      </FormField>
    </Modal>
  )
}
